// Feedback revision 2026-10-03
'use strict';
// Authored Figma states, with continuous web transitions between those states.
// All media stays local so the same build also works when opened as a file.
window.MxMotion = (() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width:767px)');
  const clamp = n => Math.min(1, Math.max(0, n));
  const mix = (a, b, t) => a + (b - a) * t;
  const ramp = (p, a, b) => { const t = clamp((p-a)/(b-a)); return t*t*(3-2*t); };
  const decode = element => Promise.all([...element.querySelectorAll('img')].map(img => {
    img.loading = 'eager'; return img.decode().catch(() => {});
  }));
  const warmNear = (element, cleanup) => {
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { decode(element); observer.disconnect(); }
    }, {rootMargin:'500px'});
    observer.observe(element); cleanup.push(()=>observer.disconnect());
  };

  function wireExperience(root, cleanup) {
    const items = [...root.querySelectorAll('.feature-item')];
    const topics=items.map(item=>({title:item.querySelector('h3').textContent,html:item.querySelector('.feature-card').innerHTML}));
    if(!topics.length)return;
    const layers = [...root.querySelectorAll('.feature-media-layer')];
    const rail = root.querySelector('.mobile-feature-rail');
    const mobileCopy = root.querySelector('.mobile-feature-card');

    let active = -1, request = 0, lastTrigger = null;
    const refined=root.classList.contains('experience-svg'),prepared=new WeakMap(),exitTimers=new Map();
    function prepare(layer){if(!prepared.has(layer))prepared.set(layer,decode(layer));return prepared.get(layer);}
    function showLayer(next){layers.forEach(layer=>{
      const shown=layer===next,wasShown=layer.classList.contains('is-visible');
      clearTimeout(exitTimers.get(layer));layer.classList.remove('is-exiting');
      if(!shown&&wasShown&&!reduced.matches){
        layer.classList.add('is-exiting');
        exitTimers.set(layer,setTimeout(()=>{layer.classList.remove('is-exiting');exitTimers.delete(layer);},250));
      }
      layer.classList.toggle('is-visible',shown);layer.setAttribute('aria-hidden',String(!shown));layer.inert=!shown;
    });}
    if(refined){
      const warmer=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){layers.forEach(prepare);warmer.disconnect();}},{rootMargin:'900px'});
      warmer.observe(root);cleanup.push(()=>{warmer.disconnect();exitTimers.forEach(clearTimeout);});
      root.querySelectorAll('[data-topic],[data-mobile-topic]').forEach(button=>{
        const warm=()=>prepare(layers[+(button.dataset.topic??button.dataset.mobileTopic)]);
        button.addEventListener('pointerenter',warm,{passive:true});button.addEventListener('focus',warm);
      });
    }
    const why=root.querySelector('[data-mobile-why]');
    function positionMobileExperience() {
      if(!mobile.matches || root.classList.contains('experience-svg'))return;
      mobileCopy.style.minHeight='0px';
      const card=active<0?0:mobileCopy.scrollHeight;
      const image=active===6?0:active===1?209:270;
      const start=60+(529-card-image-(card&&image?16:0))/2;
      mobileCopy.style.top=`${start}px`;
      mobileCopy.style.minHeight=`${card}px`;
      root.querySelector('.feature-media').style.top=`${start+card+(card&&image?16:0)}px`;
    }
    function sizeItems() {
      positionMobileExperience();
      items.forEach((item,i) => {
        item.style.height = `${i===active ? item.querySelector('.feature-card').scrollHeight : 52}px`;
      });
    }
    async function change(index, trigger, fromScroll=false) {
      if(!fromScroll && root.mxScrollTo && index>=0)root.mxScrollTo(index);
      const ticket = ++request;
      active = index;
      if (trigger?.matches('[data-topic],[data-mobile-topic]')) lastTrigger = trigger;
      root.dataset.active = String(index);
      const h2=root.querySelector('h2');h2.textContent = index>=0&&index!==1&&index!==6 ? root.dataset.expandedHeading : root.dataset.heading;h2.dataset.authoredText=h2.textContent;window.mxAuthoredHeadings?.();
      root.querySelector('.eyebrow').textContent = index>=0&&index!==1&&index!==6 ? root.dataset.expandedEyebrow : root.dataset.eyebrow;
      items.forEach((item,i) => {
        const selected = i===index;
        item.classList.toggle('is-open',selected);
        const button = item.querySelector('button'), card = item.querySelector('.feature-card');
        button.setAttribute('aria-expanded',String(selected));
        button.inert = selected;
        card.inert = !selected;
        card.setAttribute('aria-hidden',String(!selected));
      });
      const topic = topics[index===6?0:Math.max(0,index)];

      const sourceCopy=items[index===6?0:Math.max(0,index)].querySelector('.feature-card');
      mobileCopy.dataset.mxCopyKey=sourceCopy.dataset.mxCopyKey;
      mobileCopy.dataset.mxCopyLabel=sourceCopy.dataset.mxCopyLabel;
      ['x','y','mx','my'].forEach(name=>mobileCopy.setAttribute('data-mx-copy-base-'+name,sourceCopy.getAttribute('data-mx-copy-base-'+name)));
      ['--mx-copy-x','--mx-copy-y','--mx-copy-mx','--mx-copy-my'].forEach(name=>mobileCopy.style.setProperty(name,sourceCopy.style.getPropertyValue(name)));
      mobileCopy.hidden = index<0;
      mobileCopy.innerHTML = index===6 ? '<h3><button type="button" class="feature-collapse">Why now</button></h3><p>AI Redefines How We Work, MASS X Brings That Shift to the Desk.</p>' : topic.html;
      // Keep the complete merchant-provided copy for every topic.
      why?.setAttribute('aria-expanded',String(index===6));
      root.querySelector('.feature-media').hidden=mobile.matches&&index===6;
      rail.querySelectorAll('[data-mobile-topic]').forEach(b=>b.setAttribute('aria-expanded',String(+b.dataset.mobileTopic===index)));
      sizeItems();
      if (index>=0 && trigger?.matches('[data-topic]')) items[index].querySelector('.feature-collapse').focus({preventScroll:true});
      root.querySelector('.feature-status').textContent = index<0 ? 'All topics collapsed.' : topic.title;
      const next = layers[index===6?0:Math.max(0,index)];
      if(refined){
        if(![...next.querySelectorAll('img')].every(img=>img.complete&&img.naturalWidth))await prepare(next);
        if(ticket!==request)return;
        showLayer(next);
      }else{
        await decode(next);
        if(ticket!==request)return;
        layers.forEach(layer=>{const shown=layer===next;layer.classList.toggle('is-visible',shown);layer.setAttribute('aria-hidden',String(!shown));});
      }
    }
    root.addEventListener('mx:experience-ready',()=>change(Math.max(0,active),undefined,true),{once:true});
    root.querySelectorAll('[data-topic],[data-mobile-topic]').forEach(button => {
      button.addEventListener('click',()=>{const index=+(button.dataset.topic??button.dataset.mobileTopic);change(index===active?-1:index,button);});
    });
    root.querySelectorAll('[data-topic-step]').forEach(button=>button.addEventListener('click',()=>{
      const step = +button.dataset.topicStep;
      change(active<0 ? (step>0?0:topics.length-1) : (active+step+topics.length)%topics.length);
    }));
    function collapse() { change(-1); lastTrigger?.focus({preventScroll:true}); }
    root.addEventListener('click',e=>{if(e.target.closest('.feature-collapse'))collapse();});
    root.addEventListener('keydown',event=>{
      if (event.key==='Escape') collapse();
      if (event.key==='ArrowUp'||event.key==='ArrowDown') {
        event.preventDefault();
        change((Math.max(0,active)+(event.key==='ArrowUp'?topics.length-1:1))%topics.length);
      }
    });
    why?.addEventListener('click',()=>change(active===6?-1:6,why));
    root.mxSelect=index=>change(index);
    if(root.classList.contains('experience-svg'))change(0);
    wireExperienceWheel(root,()=>active,index=>change(index,undefined,true),cleanup);
    document.fonts.ready.then(()=>{if(root.isConnected)sizeItems();});
    window.addEventListener('resize',sizeItems); cleanup.push(()=>window.removeEventListener('resize',sizeItems));
    warmNear(root,cleanup);
  }

  function wireExperienceWheel(root,current,select,cleanup) {
    window.MxExperienceScroll?.(root,current,select,cleanup);
  }

  function wireMassOS(root, cleanup) {
    if (root.hasAttribute('data-mini-app-os')) return;
    const tabs=[...root.querySelectorAll('[role=tab]')];
    if(!tabs.length)return;
    const c={slides:tabs.map(t=>({label:t.textContent}))};
    const panels=[...root.querySelectorAll('.os-panel')], captions=[...root.querySelectorAll('.os-caption')];
    const rail=root.querySelector('.os-tabs-viewport'), indicator=root.querySelector('.os-tab-indicator');
    let active=0, request=0;
    function sizeOS(index) {
      const m=mobile.matches;
      const heights=m?[192.375,240,240,240,240,240,526,240,192.375]:[552,552,552,384,384,384,552,552,552];
      root.querySelector('.os-stage').style.height=`${heights[index]}px`;
      root.querySelector('.os-captions').hidden=m&&[3,4,5,6].includes(index);
      root.dataset.osDetail='false';root.querySelector('.os-stage').hidden=false;
    }
    function placeIndicator() {
      const button=tabs[active];
      indicator.style.width=`${button.offsetWidth}px`;
      indicator.style.transform=`translateX(${button.offsetLeft}px)`;
      root.querySelector('.os-captions').style.height=`${captions[active].scrollHeight}px`;
    }
    async function change(index,focus=false) {
      active=(index+c.slides.length)%c.slides.length;
      const desired=active, ticket=++request;
      tabs.forEach((t,i)=>{t.setAttribute('aria-selected',String(i===active));t.tabIndex=i===active?0:-1;});
      root.dataset.slide=String(active);
      sizeOS(active);
      placeIndicator();
      const button=tabs[active];
      rail.scrollTo({left:Math.max(0,button.offsetLeft-(rail.clientWidth-button.offsetWidth)/2),behavior:reduced.matches?'instant':'smooth'});
      if(focus) button.focus({preventScroll:true});
      await decode(panels[desired]);
      if(ticket!==request)return;
      panels.forEach((panel,i)=>{const shown=i===desired;panel.classList.toggle('is-visible',shown);panel.inert=!shown;panel.setAttribute('aria-hidden',String(!shown));});
      captions.forEach((caption,i)=>{caption.classList.toggle('is-visible',i===desired);caption.setAttribute('aria-hidden',String(i!==desired));});
      root.querySelector('.os-status').textContent=c.slides[desired].label;
    }
    tabs.forEach((tab,i)=>tab.addEventListener('click',()=>change(i)));
    root.querySelector('[role=tablist]').addEventListener('keydown',e=>{
      const next=e.key==='ArrowRight'?active+1:e.key==='ArrowLeft'?active-1:e.key==='Home'?0:e.key==='End'?tabs.length-1:null;
      if(next!==null){e.preventDefault();change(next,true);}
    });
    wireSwipe(root.querySelector('.os-stage'),step=>change(active+step),cleanup);
    const resizeOS=()=>{sizeOS(active);placeIndicator();};window.addEventListener('resize',resizeOS);cleanup.push(()=>window.removeEventListener('resize',resizeOS));
    panels.forEach((panel,i)=>{
      if(![3,4,5].includes(i))return;
      panel.tabIndex=0;panel.setAttribute('role','button');
      panel.setAttribute('aria-label','Read '+c.slides[i].label);
      const toggle=()=>{if(!mobile.matches)return;const expanded=root.dataset.osDetail!=='true';root.dataset.osDetail=String(expanded);root.querySelector('.os-stage').hidden=expanded;root.querySelector('.os-captions').hidden=!expanded;};
      panel.addEventListener('click',toggle);
      panel.addEventListener('keydown',e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();toggle();}});
    });
    root.querySelector('.os-captions').addEventListener('click',()=>{if(mobile.matches&&root.dataset.osDetail==='true'){root.dataset.osDetail='false';root.querySelector('.os-stage').hidden=false;root.querySelector('.os-captions').hidden=true;}});
    root.mxSelect=index=>change(index); document.fonts.ready.then(()=>{if(root.isConnected)placeIndicator();});
    warmNear(root,cleanup);
    sizeOS(active);placeIndicator();
  }

  function wireExtensions(root, cleanup) {
    if (root.hasAttribute('data-four-modules')) return window.MxFourModules(root, cleanup);
    const canvas=root.querySelector('.extension-canvas');
    const cards=[...root.querySelectorAll('.morph-card')].map(el=>({el,crop:el.querySelector('.morph-crop'),img:el.querySelector('.morph-image'),detail:el.querySelector('.morph-detail'),label:el.querySelector('.morph-label'),statement:el.querySelector('.morph-statement')}));
    const titles=[...root.querySelectorAll('.extension-titles h2')], intro=root.querySelector('.extension-intro');
    const heading=root.querySelector('.extension-heading'), overview=root.querySelector('.extension-copy-overview');
    const choices=root.querySelector('.extension-copy-choices'), outro=root.querySelector('.extension-outro');
    const dots=[...root.querySelectorAll('[data-extension-stage]')];
    let progress=0,target=0,raf=0,lastTime=0,active=-1,range=1,top=0;
    const stops=[0,.49,1];
    let snapped=0;
    function draw(p) {
      const split=ramp(p,.10,.42), fan=ramp(p,.56,.88), m=mobile.matches;
      canvas.dataset.progress=p.toFixed(4);
      const phase=p<.28?0:p<.72?1:2;
      canvas.dataset.phase=String(phase);
      titles[0].style.opacity=String(1-ramp(split,.2,.75));
      titles[1].style.opacity=String(ramp(split,.25,.8));
      heading.style.opacity=String(1-ramp(fan,0,.5));
      intro.style.opacity=String(1-ramp(split,0,.4));
      overview.style.opacity=String(1-ramp(split,0,.5));
      choices.style.opacity=String(ramp(split,.55,1)*(1-ramp(fan,0,.45)));
      outro.style.opacity=String(ramp(fan,.55,1));
      outro.style.transform=`translateY(${mix(20,0,ramp(fan,.55,1))}px)`;
      // Three adjacent crops initially share one continuous, full-width source image.
      // The same DOM cards then separate, recrop, and become the final statement cards.
      cards.forEach((card,i)=>{
        if(card.detail)card.detail.style.opacity=String(ramp(split,.4,1));
        const w0=m?122:416,w1=m?110:344,w2=m?140:344;
        const h0=m?270:536,h1=m?280:520,h2=m?235:440;
        const x0=(m?12:96)+w0*i, y0=m?178:193.333;
        const x1=(m?18:180)+(m?122:368)*i,y1=m?170:187;
        const x2=m?[125,19.6715,226.9615][i]:[548,278,818][i];
        const y2=m?[166,194.1185,193.5985][i]:[168,202,202][i];
        const w=mix(mix(w0,w1,split),w2,fan),h=mix(mix(h0,h1,split),h2,fan);
        card.el.style.width=`${w}px`;card.el.style.height=`${h}px`;
        card.el.style.transform=`translate3d(${mix(mix(x0,x1,split),x2,fan)}px,${mix(mix(y0,y1,split),y2,fan)}px,0) rotate(${[0,-8,8][i]*fan}deg)`;
        card.el.style.borderRadius=`${mix(i===1?0:8,8,split)}px`;
        if(split<.01)card.el.style.borderRadius=i===0?'8px 0 0 8px':i===2?'0 8px 8px 0':'0';
        card.el.style.boxShadow=`0 ${20*fan}px ${40*fan}px rgba(0,0,0,${.32*fan})`;
        const cropH=m?170:400;
        card.crop.style.height=`${mix(h0,cropH,split)}px`;
        card.crop.style.opacity=String(1-ramp(fan,.15,.8));
        const fullW=m?432:1248,fullH=fullW/1.6;
        const startX=m?(-33-i*122):-i*416, startY=m?0:(536-fullH)/2;
        const cropScale=m?110/344:1;
        const coverScale=m?Math.max(1,cropH/([710,500,780][i]*cropScale)):1;
        const endW=[1152,1152,1600][i]*cropScale*coverScale,endH=[720,720,1000][i]*cropScale*coverScale;
        const endX=[-440,-790,-120][i]*cropScale*coverScale,endY=[-10,-220,-220][i]*cropScale*coverScale;
        card.img.style.width=`${mix(fullW,endW,split)}px`;card.img.style.height=`${mix(fullH,endH,split)}px`;
        card.img.style.transform=`translate3d(${mix(startX,endX,split)}px,${mix(startY,endY,split)}px,0)`;
        card.label.style.opacity=String(ramp(split,.6,1)*(1-ramp(fan,0,.4)));
        card.statement.style.opacity=String(ramp(fan,.15,.8));
      });
      if(phase!==active){
        active=phase;dots.forEach((dot,i)=>{dot.setAttribute('aria-current',String(i===phase));});
        root.querySelector('.extension-status').textContent=['One connected system.','Three extensions: Type Core, Voice and Num Deck.','One system. Your setup.'][phase];
        root.querySelector('[data-extension-step="-1"]').disabled=phase===0;
        root.querySelector('[data-extension-step="1"]').disabled=phase===2;
        [overview,choices,outro].forEach((el,i)=>el.setAttribute('aria-hidden',String(i!==phase)));
        cards.forEach(card=>{card.label.setAttribute('aria-hidden',String(phase!==1));card.statement.setAttribute('aria-hidden',String(phase!==2));});
      }
    }
    function frame(time) {
      const dt=Math.min(64,time-(lastTime||time-16));lastTime=time;
      progress=reduced.matches?target:mix(progress,target,1-Math.exp(-dt/125));
      if(Math.abs(progress-target)<.0001)progress=target;
      draw(progress);
      if(progress!==target)raf=requestAnimationFrame(frame);
      else {raf=0;lastTime=0;root.classList.remove('is-animating');}
    }
    function schedule() {if(!raf){root.classList.add('is-animating');raf=requestAnimationFrame(frame);}}
    function onScroll() {
      if(reduced.matches||document.body.classList.contains('mxw-editor'))return;
      const raw=clamp((top-root.getBoundingClientRect().top)/range);
      // Hysteresis avoids oscillation near a boundary; once triggered, finish
      // the whole transition even if scrolling stops between authored states.
      if(raw>.77)snapped=2;
      else if(raw<.23)snapped=0;
      else if(raw>.27&&raw<.73)snapped=1;
      target=stops[snapped];
      if(Math.abs(progress-target)>.00005)schedule();
    }
    function resize() {
      const header=document.querySelector('#header')?.getBoundingClientRect()||{height:0};
      top=Math.ceil(header.height);
      const height=mobile.matches?650:916,width=mobile.matches?390:1440;
      const available=Math.max(1,(window.visualViewport?.height||innerHeight)-top);
      const scale=Math.min(document.documentElement.clientWidth/width,available/height);
      range=Math.max(1600,innerHeight*2.8);
      root.style.setProperty('--story-scale',String(scale));
      root.style.setProperty('--story-top',`${top}px`);
      root.style.setProperty('--story-pin-height',`${available}px`);
      root.style.setProperty('--story-height',`${reduced.matches?available:available+range}px`);
      root.classList.toggle('is-reduced-motion',reduced.matches);
      draw(progress);onScroll();
    }
    function navigate(index) {
      index=Math.max(0,Math.min(2,index));
      if(reduced.matches||document.body.classList.contains('mxw-editor')){target=progress=stops[index];draw(progress);return;}
      window.scrollTo({top:scrollY+root.getBoundingClientRect().top-top+stops[index]*range,behavior:'smooth'});
    }
    dots.forEach((dot,i)=>dot.addEventListener('click',()=>navigate(i)));
    root.querySelectorAll('[data-extension-step]').forEach(button=>button.addEventListener('click',()=>navigate(active+(+button.dataset.extensionStep))));
    root.querySelector('.extension-controls').addEventListener('keydown',e=>{
      if(e.key==='ArrowLeft'||e.key==='ArrowRight'){e.preventDefault();navigate(active+(e.key==='ArrowRight'?1:-1));}
    });
    window.addEventListener('scroll',onScroll,{passive:true});
    window.addEventListener('resize',resize);
    reduced.addEventListener('change',resize);
    const headerObserver=new ResizeObserver(resize); if(document.querySelector('#header'))headerObserver.observe(document.querySelector('#header'));
    root.mxSelect=index=>navigate(index); cleanup.push(()=>{cancelAnimationFrame(raf);window.removeEventListener('scroll',onScroll);window.removeEventListener('resize',resize);reduced.removeEventListener('change',resize);headerObserver.disconnect();});
    resize();warmNear(root,cleanup);
  }
  // Pointer gestures work with mouse, pen and touch. Vertical page scrolling
  // and native video controls retain ownership of their gestures.
  function wireSwipe(stage, onStep, cleanup) {
    if(!stage)return;
    const controller=new AbortController(),options={signal:controller.signal};
    let drag=null;
    stage.classList.add('swipe-stage');
    stage.querySelectorAll('img').forEach(img=>img.draggable=false);
    stage.addEventListener('pointerdown',e=>{
      if(!e.isPrimary||e.button!==0||e.target.closest('button,a,input,textarea,select,video'))return;
      drag={id:e.pointerId,x:e.clientX,y:e.clientY,locked:false};
    },options);
    stage.addEventListener('pointermove',e=>{
      if(!drag||drag.id!==e.pointerId)return;
      const dx=e.clientX-drag.x,dy=e.clientY-drag.y;
      if(!drag.locked&&Math.abs(dy)>12&&Math.abs(dy)>Math.abs(dx)){drag=null;return;}
      if(Math.abs(dx)>12&&Math.abs(dx)>Math.abs(dy)*1.4){
        drag.locked=true;stage.setPointerCapture(e.pointerId);stage.classList.add('is-dragging');e.preventDefault();
      }
    },options);
    const end=e=>{
      if(!drag||drag.id!==e.pointerId)return;
      const dx=e.clientX-drag.x,dy=e.clientY-drag.y,completed=e.type==='pointerup';
      drag=null;stage.classList.remove('is-dragging');
      if(stage.hasPointerCapture(e.pointerId))stage.releasePointerCapture(e.pointerId);
      if(completed&&Math.abs(dx)>48&&Math.abs(dx)>Math.abs(dy)*1.4)onStep(dx<0?1:-1);
    };
    stage.addEventListener('pointerup',end,options);
    stage.addEventListener('pointercancel',end,options);
    stage.addEventListener('lostpointercapture',e=>{if(e.target===stage){drag=null;stage.classList.remove('is-dragging');}},options);
    stage.addEventListener('pointerleave',()=>{if(drag&&!drag.locked)drag=null;},options);
    cleanup.push(()=>controller.abort());
  }

  return {wireExperience,wireMassOS,wireExtensions,wireSwipe};
})();
